import React from "react";
import { Frame, Mono, Row, Dot, Count, Donut, Lockup } from "../pitch/slides";

export const LIME = "#dcff3a";
export const GREEN = "#2e6b57";
export const GREEN_2 = "#3e8b70";
export const OFFWHITE = "#f5f3ee";
export const CORAL = "#e8705f";
export const INK = "#0b1914";

export { Frame, Mono, Row, Dot, Count, Donut, Lockup };

export const G = ({ children, shift = 34, ...p }) => (
  <Frame {...p} total={31} prefix="gtm">
    <div style={{ position: "absolute", inset: 0, transform: `translateY(${shift}px)` }}>{children}</div>
  </Frame>
);

export const Card = ({ children, style, accent, d = 0, cls = "" }) => (
  <div
    className={`${accent ? "vpd-card-a" : "vpd-card"} an ${cls}`}
    style={{ padding: "18px 20px", "--d": `${d}ms`, ...style }}
  >
    {children}
  </div>
);

/* ── symbols ──────────────────────────────────────────────────────────────
   The printed deck uses chunky lime shapes with a soft extrusion behind them.
   Each glyph draws the same path twice: once offset as the shadow, once in
   lime on top. `float` makes them drift once they land. */
const SHAPES = {
  ring: (c) => <><circle cx="22" cy="22" r="15" fill="none" stroke={c} strokeWidth="13" /></>,
  bolt: (c) => <path d="M25 2 L7 25 h12 l-4 17 L37 18 H24 z" fill={c} />,
  cube: (c) => <path d="M8 14 L22 7 l14 7 v16 l-14 7 -14-7 z" fill={c} />,
  arrowUp: (c) => <path d="M22 3 L39 21 h-9 v19 H14 V21 H5 z" fill={c} />,
  play: (c) => <path d="M9 5 L39 22 L9 39 z" fill={c} />,
  octagon: (c) => <path d="M14 4 h16 l10 10 v16 l-10 10 H14 L4 30 V14 z" fill={c} />,
  hexagon: (c) => <path d="M22 3 l17 9.5 v19 L22 41 5 31.5 v-19 z" fill={c} />,
  arc: (c) => <path d="M5 34 A17 17 0 0 1 39 34" fill="none" stroke={c} strokeWidth="11" strokeLinecap="round" />,
  star: (c) => (
    <path fill={c} d={Array.from({ length: 12 }, (_, k) => {
      const r = k % 2 ? 9.5 : 19.5;
      const a = (Math.PI / 6) * k - Math.PI / 2;
      return `${k ? "L" : "M"}${(22 + r * Math.cos(a)).toFixed(1)} ${(22 + r * Math.sin(a)).toFixed(1)}`;
    }).join(" ") + " Z"} />
  ),
  flag: (c) => <path d="M6 6 h22 l8 10 -8 10 H6 z" fill={c} />,
  note: (c) => <path d="M6 6 h32 v24 l-8 8 H6 z" fill={c} />,
  arrowRight: (c) => <path d="M5 16 h18 V7 l16 15 -16 15 v-9 H5 z" fill={c} />,
};

export const Glyph = ({ kind = "ring", size = 44, d = 0, label, float = true, style }) => {
  const S = SHAPES[kind] || SHAPES.ring;
  return (
    <div className="an-s" style={{ width: size, height: size, "--d": `${d}ms`, ...style }} aria-hidden="true">
      <div className={`gtm-glyph ${float ? "gtm-float" : ""}`} style={{ width: size, height: size, "--d": `${d}ms` }}>
        <svg viewBox="0 0 44 44" width={size} height={size} style={{ overflow: "visible" }}>
          <g transform="translate(2.6,3.4)" opacity="0.5">{S("#2a4a1f")}</g>
          {S(LIME)}
        </svg>
        {label ? <span className="gtm-glyph-label m">{label}</span> : null}
      </div>
    </div>
  );
};

/* NET 60 style badge — a rounded lime tile with text inside */
export const Badge = ({ lines = ["NET", "60"], size = 44, d = 0, style }) => (
  <div className="an-s" style={{ width: size, height: size, "--d": `${d}ms`, ...style }} aria-hidden="true">
    <div className="gtm-glyph gtm-float" style={{ width: size, height: size, "--d": `${d}ms` }}>
      <svg viewBox="0 0 44 44" width={size} height={size} style={{ overflow: "visible" }}>
        <rect x="2.6" y="3.4" width="40" height="40" rx="9" fill="#2a4a1f" opacity="0.5" />
        <rect x="0" y="0" width="40" height="40" rx="9" fill={LIME} />
        {lines.map((l, i) => (
          <text key={l} x="20" y={i ? 29 : 18} textAnchor="middle" fill={INK}
            style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 11, fontWeight: 700 }}>{l}</text>
        ))}
      </svg>
    </div>
  </div>
);

/* dark circle holding a step number, as on the printed cards */
export const NumBadge = ({ n, size = 26, d = 0, style }) => (
  <div className="gtm-num an-s" style={{ width: size, height: size, "--d": `${d}ms`, ...style }}>
    <span className="m" style={{ fontSize: size * 0.42, color: LIME }}>{n}</span>
  </div>
);

/* chevron between cards, as on slide 3 */
export const Chevron = ({ d = 0, style }) => (
  <span className="an" style={{ display: "block", lineHeight: 0, "--d": `${d}ms`, ...style }} aria-hidden="true">
    <svg className="gtm-chev" width="13" height="24" viewBox="0 0 13 24">
      <path d="M2 2 L11 12 L2 22" fill="none" stroke={LIME} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

/* ── flywheel (slide 2) ───────────────────────────────────────────────────
   Dark hub, a ring track, three lime nodes on it and three arrowheads that
   turn slowly so the wheel reads as always spinning. */
export const Flywheel = ({ cx, cy, r = 92, hub = 62, label, sub, d = 240, nodes = 3 }) => {
  const at = (deg, rad = r) => [cx + rad * Math.cos((deg * Math.PI) / 180), cy + rad * Math.sin((deg * Math.PI) / 180)];
  return (
    <svg width="960" height="540" style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none", overflow: "visible" }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,.14)" strokeWidth="11"
        className="dash" style={{ "--len": 2 * Math.PI * r, "--d": `${d}ms` }} />
      <g className="gtm-spin" style={{ transformOrigin: `${cx}px ${cy}px` }}>
        {[-30, 90, 210].map((deg, i) => {
          const [x, y] = at(deg);
          return (
            <g key={deg} transform={`rotate(${deg + 90} ${x.toFixed(1)} ${y.toFixed(1)}) translate(${x.toFixed(1)} ${y.toFixed(1)})`}>
              <g className="fade" style={{ "--d": `${d + 520 + i * 90}ms` }}>
                <path d="M0 -9 L9 9 L-9 9 Z" fill={LIME} opacity="0.92" />
              </g>
            </g>
          );
        })}
      </g>
      {Array.from({ length: nodes }).map((_, i) => {
        const deg = -90 + i * (360 / nodes);
        const [x, y] = at(deg);
        return (
          <g key={deg} className="an-s" style={{ "--d": `${d + 300 + i * 120}ms`, transformOrigin: `${x}px ${y}px` }}>
            <circle cx={x} cy={y} r="13" fill={LIME} />
            <circle cx={x} cy={y} r="19" fill="none" stroke={LIME} strokeWidth="1" opacity="0.28" />
          </g>
        );
      })}
      <g className="an-s" style={{ "--d": `${d + 140}ms`, transformOrigin: `${cx}px ${cy}px` }}>
        <circle cx={cx} cy={cy} r={hub} fill="#0d1c17" />
        <text x={cx} y={cy - 3} textAnchor="middle" className="h" style={{ fontSize: 16, fill: LIME }}>{label}</text>
        {sub && <text x={cx} y={cy + 18} textAnchor="middle" className="h" style={{ fontSize: 16, fill: LIME }}>{sub}</text>}
      </g>
    </svg>
  );
};

/* ── spokes (slide 4) ─────────────────────────────────────────────────────
   Dashed lines from each card to the hub, drawn in one after another. */
export const Spokes = ({ hub, points, d = 420 }) => (
  <svg width="960" height="540" style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none" }}>
    {points.map(([x, y], i) => (
      <g key={i}>
        {/* the draw-in lives on the wrapper: .dash would overwrite the dash pattern */}
        <g className="fade" style={{ "--d": `${d + i * 70}ms` }}>
          <line x1={x} y1={y} x2={hub[0]} y2={hub[1]} stroke="rgba(220,255,58,.34)" strokeWidth="1.3"
            strokeDasharray="5 6" />
        </g>
        <circle cx={x} cy={y} r="3" fill={LIME} className="an-s" style={{ "--d": `${d + 220 + i * 70}ms`, transformOrigin: `${x}px ${y}px` }} />
      </g>
    ))}
  </svg>
);

/* value + caption stat */
export const Stat = ({ v, l, size = 24, d = 0, w, lime, style }) => (
  <div className="an" style={{ width: w, "--d": `${d}ms`, ...style }}>
    <div className={`h ${lime ? "lime" : ""}`} style={{ fontSize: size }}>{v}</div>
    <div style={{ fontSize: 10, color: "#8ea096", marginTop: 8, lineHeight: "13px" }}>{l}</div>
  </div>
);

/* label → value line used by exit gates and P&L */
export const KV = ({ k, v, d = 0, lime, size = 10.5, kSize = 9.5 }) => (
  <Row className="an" style={{ justifyContent: "space-between", height: 37.4, "--d": `${d}ms` }}>
    <span style={{ fontSize: kSize, color: "#9fb0a6" }}>{k}</span>
    <span className="h" style={{ fontSize: size, color: lime ? LIME : "#fff" }}>{v}</span>
  </Row>
);

const path = (pts) => pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");

/* animated line + area chart */
export const LineChart = ({
  x, y, w, h, data, max, ticks, fmtTick, labels, color = LIME, area = true,
  d = 220, gradId = "gtmArea", zero = 0, marks = [],
}) => {
  const span = max - zero;
  const px = (i) => x + (w * i) / (data.length - 1);
  const py = (v) => y + h - ((v - zero) / span) * h;
  const pts = data.map((v, i) => [px(i), py(v)]);
  return (
    <svg width="960" height="540" style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none" }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.26" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {ticks.map((t) => (
        <g key={t}>
          <line x1={x} y1={py(t)} x2={x + w} y2={py(t)} stroke="rgba(255,255,255,.08)" strokeWidth="1" />
          <text x={x - 8} y={py(t) + 3} textAnchor="end" className="m" style={{ fontSize: 8, fill: "#5c6e65" }}>{fmtTick(t)}</text>
        </g>
      ))}
      {area && (
        <path
          d={`${path(pts)} L${px(data.length - 1)} ${py(zero)} L${x} ${py(zero)} Z`}
          fill={`url(#${gradId})`} className="fade" style={{ "--d": `${d + 360}ms` }}
        />
      )}
      <path
        d={path(pts)} fill="none" stroke={color} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"
        className="dash" style={{ "--len": 1600, "--d": `${d}ms` }}
      />
      {marks.map((m, k) => (
        <g key={k} className="an-s" style={{ "--d": `${d + 700 + k * 120}ms`, transformOrigin: `${px(m.i)}px ${py(data[m.i])}px` }}>
          <circle cx={px(m.i)} cy={py(data[m.i])} r={6} fill={m.c || color} />
          {m.label && (
            <text x={px(m.i) + (m.dx || 10)} y={py(data[m.i]) + (m.dy || -12)} className="h" style={{ fontSize: 11, fill: m.c || "#fff" }}>{m.label}</text>
          )}
        </g>
      ))}
      {labels && labels.map((l, i) =>
        l ? (
          <text key={i} x={px(i)} y={y + h + 16} textAnchor="middle" className="m" style={{ fontSize: 7.5, fill: "#5c6e65" }}>{l}</text>
        ) : null
      )}
    </svg>
  );
};

/* stacked monthly bars */
export const StackBars = ({ x, y, w, h, series, max, ticks, fmtTick, labels, d = 220 }) => {
  const n = series[0].v.length;
  const bw = (w / n) * 0.62;
  const gap = w / n;
  return (
    <div style={{ position: "absolute", left: x, top: y, width: w, height: h + 26 }}>
      {ticks.map((t) => (
        <React.Fragment key={t}>
          <div style={{ position: "absolute", left: 0, top: h - (t / max) * h, width: w, height: 1, background: "rgba(255,255,255,.07)" }} />
          <span className="m" style={{ position: "absolute", left: -34, top: h - (t / max) * h - 5, fontSize: 8, color: "#5c6e65" }}>{fmtTick(t)}</span>
        </React.Fragment>
      ))}
      {Array.from({ length: n }).map((_, i) => {
        let acc = 0;
        return (
          <div key={i} style={{ position: "absolute", left: i * gap + (gap - bw) / 2, bottom: 26, width: bw }}>
            {series.map((s, k) => {
              const seg = (s.v[i] / max) * h;
              const bottom = acc;
              acc += seg;
              return (
                <div
                  key={s.n}
                  className="gh"
                  style={{
                    position: "absolute", bottom, width: bw, height: seg, background: s.c,
                    borderRadius: k === series.length - 1 ? "3px 3px 0 0" : 0,
                    "--h": `${seg}px`, "--d": `${d + i * 26}ms`,
                  }}
                />
              );
            })}
            {labels[i] ? (
              <span className="m" style={{ position: "absolute", top: 8, left: -6, width: bw + 12, textAlign: "center", fontSize: 7.5, color: "#5c6e65", whiteSpace: "pre-line" }}>{labels[i]}</span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export const Legend = ({ items, style }) => (
  <Row className="an" style={{ gap: 18, "--d": "900ms", ...style }}>
    {items.map((i) => (
      <Row key={i.n} style={{ gap: 7 }}>
        <Dot c={i.c} s={8} />
        <span style={{ fontSize: 9, color: "#9fb0a6" }}>{i.n}</span>
      </Row>
    ))}
  </Row>
);
